import { useMemo } from 'react';
import clsx from 'clsx';
import { useSimulationStore } from '@/state/simulationStore';
import { customerName, simTimeFull } from '@/utils/format';
import { AWAITING_CLIENT } from '@/domain/selfBookFlow';
import { deriveDepositStatus, DEPOSIT_STATUS_META } from '@/domain/depositStatus';
import type { CustomerRecord, Deposit, SelfBookLink } from '@/domain/types';

/**
 * Artists read "did they pay?", not the office's ledger distinction between a
 * paid deposit and one placed against a booking — so `placed` borrows the `paid`
 * chip wholesale (label, tone and icon). The Deposits ledger keeps both states.
 */
const ARTIST_STATUS_META: typeof DEPOSIT_STATUS_META = {
  ...DEPOSIT_STATUS_META,
  placed: DEPOSIT_STATUS_META.paid,
};

interface Props {
  deposits: Deposit[];
  customers: Record<string, CustomerRecord>;
  /**
   * B5 (8xar): the artist's LINKS — named links sent to a client (a named link
   * has no deposit until it is paid, so a deposit-only list never showed it) and
   * the universal link, whose pinned hold shows as "Held · awaiting client".
   */
  links?: SelfBookLink[];
  /** Every deposit by id, to read a pin's hold. */
  depositsById?: Record<string, Deposit>;
}

type LinkChip = { label: string; tone: string };

/** A named link's state for the artist, read-only (the Agent follows an expired one up). */
function linkChip(l: SelfBookLink, nowMs: number): LinkChip {
  if (l.status === 'paid') return { label: 'Paid', tone: 'bg-mint/20 text-mint' };
  if (l.status === 'expired' || new Date(l.expiresAt).getTime() <= nowMs) {
    return l.replacedByLinkId
      ? { label: 'Expired · replaced', tone: 'bg-cream-soft/15 text-cream-soft' }
      : { label: 'Expired', tone: 'bg-blood/20 text-blood' };
  }
  if (l.status === 'viewed') return { label: 'Viewed', tone: 'bg-gold/20 text-gold-bright' };
  return { label: 'Sent', tone: 'bg-cream-soft/15 text-cream-soft' };
}

export default function SelfBookStatusList({ deposits, customers, links = [], depositsById = {} }: Props) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const nowMs = new Date(clockIso).getTime();

  const rows = useMemo(
    () => [...deposits].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [deposits],
  );
  const named = useMemo(
    () =>
      links
        .filter((l) => !l.universal && l.customerId)
        .sort((a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()),
    [links],
  );
  const pins = links
    .filter((l) => l.universal && l.pinnedStartIso && l.pinnedDepositId)
    .map((l) => ({ link: l, hold: depositsById[l.pinnedDepositId!] }))
    .filter((p) => !p.hold || p.hold.status === 'pending');

  if (rows.length === 0 && named.length === 0 && pins.length === 0) {
    return <p className="font-serif text-cream-soft text-sm">No links sent yet. Hold a slot above to create one.</p>;
  }

  return (
    <ul className="flex flex-col gap-2" data-testid="self-book-status-list">
      {pins.map(({ link: l }) => (
        <li
          key={`pin-${l.token}`}
          className="rounded-lg border border-gold-bright/40 bg-ink-black px-3 py-2.5"
          data-testid={`self-book-pin-${l.pinnedDepositId}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-inter text-sm font-semibold text-cream">Awaiting client</p>
              <p className="font-mono text-[10px] text-cream-faint mt-0.5">{simTimeFull(l.pinnedStartIso!)}</p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-inter font-bold uppercase tracking-widest bg-gold/20 text-gold-bright">
              Held · awaiting client
            </span>
          </div>
        </li>
      ))}
      {named.map((l) => {
        const chip = linkChip(l, nowMs);
        const cust = l.customerId ? customers[l.customerId] : undefined;
        return (
          <li
            key={`link-${l.token}`}
            className="rounded-lg border border-gold-dim/28 bg-ink-black px-3 py-2.5"
            data-testid={`self-book-link-${l.token}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-inter text-sm font-semibold text-cream">{cust ? customerName(cust) : 'Client'}</p>
                <p className="font-mono text-[10px] text-cream-faint mt-0.5">
                  Sent {simTimeFull(l.generatedAt)} · expires {simTimeFull(l.expiresAt)}
                </p>
              </div>
              <span
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-inter font-bold uppercase tracking-widest',
                  chip.tone,
                )}
              >
                {chip.label}
              </span>
            </div>
          </li>
        );
      })}
      {rows.map((d) => {
        const status = deriveDepositStatus(d, nowMs);
        const meta = ARTIST_STATUS_META[status];
        const Icon = meta.icon;
        const isAwaitingClient = d.customerId === AWAITING_CLIENT;
        const cust = isAwaitingClient ? undefined : customers[d.customerId];
        const nameLabel = isAwaitingClient ? 'Awaiting client' : cust ? customerName(cust) : 'Client';
        return (
          <li
            key={d.id}
            className="rounded-lg border border-gold-dim/28 bg-ink-black px-3 py-2.5"
            data-testid={`self-book-status-${d.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-inter text-sm font-semibold text-cream">{nameLabel}</p>
                <p className="font-mono text-[10px] text-cream-faint mt-0.5">
                  {simTimeFull(d.createdAt)} · <span className="text-gold">{d.depositReference}</span>
                </p>
              </div>
              <span
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-inter font-bold uppercase tracking-widest',
                  meta.tone,
                )}
              >
                <Icon size={10} /> {meta.label}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
